import React, { useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Stack,
  TextareaAutosize,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import { ASSET_IMAGES } from 'app/utils/constants/paths';
import axios from 'axios';

const Home = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [baseData, setBaseData] = useState();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = () => {
    fileInputRef.current.click(); // Trigger the hidden file input
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    console.log(file);

    if (!file) return;

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Only Excel files are allowed.');
      return;
    }
    setLoading(true);
    try {
      const fromData = new FormData();
      fromData.append('upload_file', file);
      const response = await axios.post('/ai/spelling-checker', fromData);
      if (response.status === 200) {
        setBaseData(response?.data?.data?.result?.base64_file);
        setLoading(false);
        fileInputRef.current.value = ''; // Clear the file input
      }
    } catch (error) {
      setLoading(false);
      setBaseData(null);
      enqueueSnackbar(error?.data?.message, {
        variant: 'error',
      });
    }
  };
  const textAreaRef = useRef();

  const handleCopy = () => {
    textAreaRef.current.select();
    document.execCommand('copy');
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      console.log('Pasted text:', text);
      if (textAreaRef.current) {
        textAreaRef.current.value += text;
      }
    } catch (err) {
      console.error('Failed to read clipboard: ', err);
      alert(
        'Clipboard access blocked. Please check browser permissions and use HTTPS.',
      );
    }
  };

  const handleClear = () => {
    textAreaRef.current.value = '';
    setBaseData(null);
  };

  const handleGenerateReport = async () => {
    if (!textAreaRef.current.value) {
      alert('Please enter text before generating the report.');
      return;
    }
    setLoading(true);
    try {
      const fromData = new FormData();
      fromData.append('pasted_text', textAreaRef.current.value);
      const response = await axios.post('/ai/spelling-checker', fromData);
      if (response.status === 200) {
        setBaseData(response?.data?.data?.result?.base64_file);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      setBaseData(null);
      enqueueSnackbar(error?.data?.message, {
        variant: 'error',
      });
    }
  };
  const handleDownload = () => {
    const byteCharacters = atob(baseData);
    const byteNumbers = Array.from(byteCharacters).map((char) =>
      char.charCodeAt(0),
    );
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: 'application/xlsx',
    });

    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'corrected.xlsx';
    link.click();

    // Cleanup
    URL.revokeObjectURL(url);
  };

  return (
    <center>
      <Stack
        justifyContent="space-between"
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          width: '100%',
          maxWidth: '800px',
          background: '#D3D04F',
          padding: '20px 20px',
          borderRadius: '15px 15px 0 0',
          alignItems: 'center',
        }}
      >
        <img
          style={{ width: '110px', padding: '0' }}
          src={`${ASSET_IMAGES}/tsm-logo.png`}
          alt="Jumbo React"
        />
        <Typography
          variant={'h1'}
          mb={0}
          sx={{ fontSize: '32px', fontWeight: '500' }}
        >
          TSM Spell / Sentence Check
        </Typography>
        <div></div>
        <div></div>
      </Stack>
      <Card
        sx={{
          width: '100%',
          maxWidth: '800px',
          textAlign: 'center',
          padding: '0 20px ',
          borderRadius: '0 0 15px 15px',
        }}
      >
        <CardContent>
          <Stack
            justifyContent="right"
            direction="row"
            spacing={1}
            sx={{ mb: 1, mt: -2 }}
          >
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Tooltip title="File Upload">
              <IconButton onClick={handleUpload}>
                <CloudUploadIcon sx={{ fontSize: '19px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Text">
              <IconButton onClick={handleCopy}>
                <ContentCopyIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Paste Text">
              <IconButton onClick={handlePaste}>
                <ContentPasteIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear Text">
              <IconButton onClick={handleClear}>
                <ClearIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <TextareaAutosize
            ref={textAreaRef}
            maxRows={4}
            aria-label="maximum height"
            placeholder="Paste your content here..."
            defaultValue=""
            style={{ width: '100%', height: '240px' }}
          />

          <LoadingButton
            loading={loading}
            onClick={() => {
              handleGenerateReport();
            }}
            fullWidth
            type="submit"
            variant="contained"
            mt={2}
          >
            Generate Report
          </LoadingButton>
          {baseData && (
            <LoadingButton
              onClick={() => {
                handleDownload();
              }}
              sx={{
                mt: '10px',
                backgroundColor: '#10b981',
                '&:hover': {
                  backgroundColor: '#4fa789',
                },
              }}
              width={'100'}
              type="submit"
              variant="contained"
            >
              Download Corrected File
            </LoadingButton>
          )}
        </CardContent>
      </Card>
    </center>
  );
};

export default Home;
